import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spinner, Form, Badge, Alert } from 'react-bootstrap';
import useToken from 'hooks/useToken';
import FalconCloseButton from 'components/common/FalconCloseButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';
import ModalConfirmacionPagoSuscripcion from 'akasia-components/licencia/Modales/ModalConfirmacionPagoSuscripcion';

const ModalNuevaSuscripcion = ({
    show,
    setShow,
    idCliente,
    onSuscripcionCreada
}) => {
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    // Estados principales
    const [productos, setProductos] = useState([]);
    const [productoSeleccionado, setProductoSeleccionado] = useState(null);
    const [renovaciones, setRenovaciones] = useState([]);
    const [renovacionSeleccionada, setRenovacionSeleccionada] = useState(null);
    const [nombreSuscripcion, setNombreSuscripcion] = useState('');
    const [monedaSeleccionada, setMonedaSeleccionada] = useState('MXN');
    const [cargando, setCargando] = useState(false);

    const [tiposLicencia, setTiposLicencia] = useState([]);
    const [tipoLicenciaSeleccionado, setTipoLicenciaSeleccionado] = useState(null);
    const [licenciasServidor, setLicenciasServidor] = useState([]);
    const [licenciaServidorVinculada, setLicenciaServidorVinculada] = useState(null);
    const [cargandoLicenciasServidor, setCargandoLicenciasServidor] = useState(false);
    const [vUUIDLicencia, setVUUIDLicencia] = useState('');
    const [vUUIDEmpresa, setVUUIDEmpresa] = useState('');
    const [vLatLon, setVLatLon] = useState('');

    // Estado para días de periodo de prueba
    const [iDiasPeriodoPrueba, setIDiasPeriodoPrueba] = useState(5); // Default: 5 días

    // Estados para licencias AkasiaPOS
    const [licenciasAkasiaPOS, setLicenciasAkasiaPOS] = useState([]);
    const [licenciaAkasiaPOSSeleccionada, setLicenciaAkasiaPOSSeleccionada] = useState(null);
    const [cargandoLicenciasAkasiaPOS, setCargandoLicenciasAkasiaPOS] = useState(false);

    // Estados para precio editable
    const [precioTotal, setPrecioTotal] = useState(0);
    const [precioUnitario, setPrecioUnitario] = useState(0);
    const [precioIVA, setPrecioIVA] = useState(0);

    // Estados para modal de confirmación de pago (cuando existe suscripción)
    const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
    const [infoSuscripcionExistente, setInfoSuscripcionExistente] = useState(null);
    const [payloadSuscripcion, setPayloadSuscripcion] = useState(null);

    // Estado para buscador de productos
    const [busquedaProducto, setBusquedaProducto] = useState('');

    /**
     * Filtra productos por código de barras y nombre simultáneamente
     */
    const productosFiltrados = productos.filter((producto) => {
        if (!busquedaProducto.trim()) return true;
        
        const busqueda = busquedaProducto.toLowerCase();
        const nombreMatch = producto.vNombreProducto?.toLowerCase().includes(busqueda);
        const codigoMatch = producto.vCodigoBarras?.toLowerCase().includes(busqueda);
        
        return nombreMatch || codigoMatch;
    });

    /**
     * Mapea el tipo de renovación al idCatCicloFacturacion correspondiente
     * 1 = MENSUAL
     * 2 = SEMESTRAL
     * 3 = ANUAL
     * 4 = PERMANENTE
     */
    const obtenerIdCicloFacturacion = (tipoRenovacion) => {
        const mapeo = {
            'mensual': 1,
            'semestral': 2,
            'anual': 3,
            'permanente': 4
        };
        return mapeo[tipoRenovacion.toLowerCase()] || 1; // Default: Mensual
    };

    /**
     * Recalcula precio unitario e IVA cuando se edita el precio total
     */
    const recalcularPrecios = (total) => {
        const totalNum = parseFloat(total) || 0;
        setPrecioTotal(totalNum);
        
        // Fórmula inversa: dPrecioUnitario = dTotal / 1.16
        const unitario = totalNum / 1.16;
        const iva = totalNum - unitario;
        
        setPrecioUnitario(unitario);
        setPrecioIVA(iva);
    };

    const handleClose = () => {
        setProductoSeleccionado(null);
        setRenovaciones([]);
        setRenovacionSeleccionada(null);
        setNombreSuscripcion('');
        setMonedaSeleccionada('MXN');
        setTipoLicenciaSeleccionado(null);
        setLicenciaServidorVinculada(null);
        setVUUIDLicencia('');
        setVUUIDEmpresa('');
        setVLatLon('');
        setIDiasPeriodoPrueba(5);
        setLicenciaAkasiaPOSSeleccionada(null);
        setBusquedaProducto('');
        setPrecioTotal(0);
        setPrecioUnitario(0);
        setPrecioIVA(0);
        setShow(false);
    };

    /**
     * Obtiene los tipos de licencia del catálogo
   */
    const obtenerTiposLicencia = async () => {
        try {
            const response = await fetch('/api/Suscripciones/ObtenerTipoLicencias', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                // El SP ahora devuelve el patrón estándar con table1 (respuesta) y table2 (datos)
                if (data.table1 && data.table1.length > 0) {
                    const respuesta = data.table1[0];
                    if (respuesta.iResponseType === 1) {
                        if (data.table2 && data.table2.length > 0) {
                            setTiposLicencia(data.table2);
                            console.log('? Tipos de licencia cargados:', data.table2.length);
                        }
                    } else {
                        console.error('Error del SP:', respuesta.vMessage);
                        showErrorToast(respuesta.vMessage);
                    }
                }
            } else {
                console.error('Error al obtener tipos de licencia:', response.status);
                showErrorToast('Error al obtener tipos de licencia: ' + response.status);
            }
        } catch (error) {
            console.error('Error al obtener tipos de licencia:', error);
            showErrorToast('Error al obtener tipos de licencia: ' + error.message);
        }
    };

    /**
     * Obtiene las licencias tipo Servidor del cliente
  */
    const obtenerLicenciasServidor = async () => {
        if (!idCliente) return;

        setCargandoLicenciasServidor(true);
        try {
            const response = await fetch(`/api/Suscripciones/GetLicenciasByCliente?idCliente=${idCliente}`, {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.table2 && data.table2.length > 0) {
                    // Filtrar solo licencias tipo Servidor (idCatTipoLicencia = 1)
                    const licenciasServidorFiltradas = data.table2.filter(
                        lic => lic.idCatTipoLicencia === 1
                    );
                    setLicenciasServidor(licenciasServidorFiltradas);
                } else {
                    setLicenciasServidor([]);
                }
            }
        } catch (error) {
            console.error('Error al obtener licencias servidor:', error);
            setLicenciasServidor([]);
        } finally {
            setCargandoLicenciasServidor(false);
        }
    };



    /**
     * Obtiene las licencias AkasiaPOS del cliente
     */
    const obtenerLicenciasAkasiaPOS = async () => {
        if (!idCliente) return;

        setCargandoLicenciasAkasiaPOS(true);
        try {
            const response = await fetch(`/api/Suscripciones/GetLicenciasAkasiaPOSByCliente?idCliente=${idCliente}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.table1 && data.table1.length > 0 && data.table1[0].iResponseType === 1) {
                    setLicenciasAkasiaPOS(data.table2 || []);
                } else {
                    setLicenciasAkasiaPOS([]);
                }
            }
        } catch (error) {
            console.error('Error al obtener licencias AkasiaPOS:', error);
            setLicenciasAkasiaPOS([]);
        } finally {
            setCargandoLicenciasAkasiaPOS(false);
        }
    };

    /**
     * Genera UUIDs para nueva licencia
     */
    const generarUUIDs = async () => {
        // Si la licencia es tipo AkasiaPOS, no generar UUIDs
        if (tipoLicenciaSeleccionado && tipoLicenciaSeleccionado.idCatTipoLicencia === 7) {
            setVUUIDLicencia('');
            setVUUIDEmpresa('');
            return;
        }
        try {
            // Fetch both UUIDs in parallel
            const [licenciaRes, empresaRes] = await Promise.all([
                fetch('/api/Suscripciones/ObtenerUUIDLicenciaRandom', {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + token }
                }),
                fetch('/api/Suscripciones/ObtenerUUIDEmpresaRandom', {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + token }
                })
            ]);
            if (licenciaRes.ok) {
                let uuidLicencia = await licenciaRes.text();
                uuidLicencia = uuidLicencia.replace(/<[^>]*>/g, '').trim();
                setVUUIDLicencia(uuidLicencia);
            }
            if (empresaRes.ok) {
                let uuidEmpresa = await empresaRes.text();
                uuidEmpresa = uuidEmpresa.replace(/<[^>]*>/g, '').trim();
                setVUUIDEmpresa(uuidEmpresa);
            }
        } catch (error) {
            console.error('Error al generar UUIDs:', error);
            showErrorToast('Error al generar UUIDs de licencia');
        }
    };

    /**
     * Regenera solo el UUID de Licencia
     */
    const regenerarUUIDLicencia = async () => {
        try {
            const response = await fetch('/api/Suscripciones/ObtenerUUIDLicenciaRandom', {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token
                    }
            });
            if (response.ok) {
                let uuid = await response.text();
                uuid = uuid.replace(/<[^>]*>/g, '').trim();
                setVUUIDLicencia(uuid);
                console.log('? UUID Licencia regenerado:', uuid);
            }
        } catch (error) {
            console.error('Error al regenerar UUID Licencia:', error);
            showErrorToast('Error al regenerar UUID de licencia');
        }
    };

    /**
     * Regenera solo el UUID de Empresa
     */
    const regenerarUUIDEmpresa = async () => {
        try {
            const response = await fetch('/api/Suscripciones/ObtenerUUIDEmpresaRandom', {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token
                    }
            });
            if (response.ok) {
                let uuid = await response.text();
                uuid = uuid.replace(/<[^>]*>/g, '').trim();
                setVUUIDEmpresa(uuid);
                console.log('? UUID Empresa regenerado:', uuid);
            }
        } catch (error) {
            console.error('Error al regenerar UUID Empresa:', error);
            showErrorToast('Error al regenerar UUID de empresa');
        }
    };

    const obtenerProductos = async () => {
        try {
            const response = await fetch('/api/Suscripciones/ObtenerProductosRenovacion', {
                method: 'GET',
                headers:
                    {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    setProductos(parsed.table2 || []);
                } else {

                    showErrorToast(data.vMessage);
                }
            } else {

                showErrorToast(response.status + ' ' + response.statusText);
            }
        } catch (error) {

            showErrorToast('Hubo un problema con la petición: ' + error.message);
        }
    };

    useEffect(() => {
        if (show) {
            obtenerProductos();
            obtenerTiposLicencia();
            obtenerLicenciasServidor();
            obtenerLicenciasAkasiaPOS();
        }
    }, [show, idCliente]);

    // 🔧 Generar UUIDs cuando se selecciona un producto tipo Licencia
    useEffect(() => {
        if (productoSeleccionado && productoSeleccionado.vTipoProducto === 'Licencia') {
            generarUUIDs();
        }
    }, [productoSeleccionado]);

    // Establecer la moneda por defecto desde el producto seleccionado (si existe)
    useEffect(() => {
        if (productoSeleccionado && productoSeleccionado.vMoneda) {
            setMonedaSeleccionada(productoSeleccionado.vMoneda);
        }
    }, [productoSeleccionado]);

    // Inicializar precios cuando se selecciona una renovación
    useEffect(() => {
        if (renovacionSeleccionada) {
            recalcularPrecios(renovacionSeleccionada.dTotal);
        } else {
            setPrecioTotal(0);
            setPrecioUnitario(0);
            setPrecioIVA(0);
        }
    }, [renovacionSeleccionada]);

    const onSelectProducto = async (prod) => {
        setProductoSeleccionado(prod);
        setRenovaciones([]);
        setRenovacionSeleccionada(null);
        // Al cambiar de producto, limpiar el nombre de la suscripción
        setNombreSuscripcion('');
        // Limpiar selecciones de licencia si cambia el producto
        setLicenciaServidorVinculada(null);

        // Si el producto es tipo Licencia y tiene idCatTipoLicencia, preseleccionarlo automáticamente
        if (prod.vTipoProducto === 'Licencia' && prod.idCatTipoLicencia) {
            const tipoLicencia = tiposLicencia.find(t => t.idCatTipoLicencia === prod.idCatTipoLicencia);
            if (tipoLicencia) {
                setTipoLicenciaSeleccionado(tipoLicencia);
            } else {
                setTipoLicenciaSeleccionado(null);
            }
        } else {
            setTipoLicenciaSeleccionado(null);
        }

        // Establecer moneda por defecto desde el producto (si tiene definida)
        try {
            setMonedaSeleccionada(prod.vMoneda || 'MXN');
        } catch (e) {
            setMonedaSeleccionada('MXN');
        }

        try {
            const response = await fetch(`/api/Suscripciones/GetRenovacionesByProducto?idProducto=${prod.idProducto}`, {
                method: 'GET',
                headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    setRenovaciones(parsed.table2 || []);
                } else {

                    showErrorToast(data.vMessage);
                }
            } else {

                showErrorToast(response.status + ' ' + response.statusText);
            }
        } catch (error) {

            showErrorToast('Hubo un problema con la petición: ' + error.message);
        }
    };

    /**
     * 🔧 Determina si se debe mostrar el selector de licencia servidor
     */
    const debeVincularServidor = () => {
        if (!tipoLicenciaSeleccionado) return false;

        // Caja (2), App Comandas (4) siempre se vinculan, Nube (3) opcionalmente
        return tipoLicenciaSeleccionado.idCatTipoLicencia === 2 ||
            tipoLicenciaSeleccionado.idCatTipoLicencia === 4 ||
            tipoLicenciaSeleccionado.idCatTipoLicencia === 3;
    };

    /**
     * Determina si un ModuloExtra puede vincularse opcionalmente
     */
    const puedeVincularModuloExtra = () => {
        // Los módulos extra pueden vincularse opcionalmente a un Servidor
        return productoSeleccionado && productoSeleccionado.vTipoProducto === 'ModuloExtra';
    };

    const validarFormulario = () => {
        if (!productoSeleccionado || !renovacionSeleccionada) {
            return false;
        }

        // Validar que el precio total sea mayor a 0
        if (precioTotal <= 0) {
            return false;
        }

        if (productoSeleccionado.vTipoProducto === 'Licencia') {
            if (tipoLicenciaSeleccionado && tipoLicenciaSeleccionado.idCatTipoLicencia === 7) {
                return !!licenciaAkasiaPOSSeleccionada;
            }

            if (!tipoLicenciaSeleccionado || !vUUIDLicencia || !vUUIDEmpresa) {
                return false;
            }

            if (iDiasPeriodoPrueba < 0) {
                return false;
            }

            if (tipoLicenciaSeleccionado.idCatTipoLicencia === 1) {
                if (!vLatLon.trim()) {
                    return false;
                }

                const latLonPattern = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;
                if (!latLonPattern.test(vLatLon.trim())) {
                    return false;
                }
            }

            if ((tipoLicenciaSeleccionado.idCatTipoLicencia === 2 ||
                tipoLicenciaSeleccionado.idCatTipoLicencia === 4) &&
                !licenciaServidorVinculada) {
                return false;
            }
        }

        return true;
    };

    // Auto-llenar el nombre de la suscripción para que el vendedor lo vea y pueda editarlo
    useEffect(() => {
        if (!renovacionSeleccionada) return;
        // No sobreescribir si el vendedor ya escribió algo
        if (nombreSuscripcion && nombreSuscripcion.trim() !== '') return;

        const tipo = renovacionSeleccionada.vTipoRenovacion || '';
        const tipoCapitalizado = tipo.charAt(0).toUpperCase() + tipo.slice(1).toLowerCase();
        const monedaFinal = monedaSeleccionada || (productoSeleccionado && productoSeleccionado.vMoneda) || 'MXN';
        const generated = `Suscripcion ${tipoCapitalizado} ${monedaFinal}`;

        setNombreSuscripcion(generated);
    }, [renovacionSeleccionada, monedaSeleccionada, productoSeleccionado]);

    const handleSeleccionar = async () => {
        if (!validarFormulario()) return;
        setCargando(true);

        // Construir objeto producto según el tipo
        const productoData = {
            idProducto: productoSeleccionado.idProducto,
            idProductoRenovaciones: renovacionSeleccionada.idProductoRenovaciones,
            dPrecioUnitario: precioUnitario, // Usar precio recalculado
            dTasaCuotaIVA: 0.16,
            vTipoFactorIVA: 'Tasa',
            dIVA: precioIVA, // Usar IVA recalculado
            dTotal: precioTotal, // Usar precio total editado
            vTipoRenovacion: renovacionSeleccionada.vTipoRenovacion
        };

        if (productoSeleccionado.vTipoProducto === 'Licencia') {
            productoData.idCatTipoLicencia = tipoLicenciaSeleccionado.idCatTipoLicencia;

            if (tipoLicenciaSeleccionado.idCatTipoLicencia === 7) {
                productoData.idLicencia = licenciaAkasiaPOSSeleccionada.idLicencia;
                productoData.vUUIDLicencia = licenciaAkasiaPOSSeleccionada.vUUIDLicencia;
                productoData.vUUIDEmpresa = '';
                productoData.vLatLon = '';
                productoData.idCatCicloFacturacion = obtenerIdCicloFacturacion(renovacionSeleccionada.vTipoRenovacion);
                productoData.iDiasPeriodoPrueba = 0;
            } else {
                productoData.vUUIDLicencia = vUUIDLicencia;

                if (tipoLicenciaSeleccionado.idCatTipoLicencia === 3) {
                    productoData.vUUIDEmpresa = licenciaServidorVinculada ? licenciaServidorVinculada.vUUIDEmpresa : '';
                } else {
                    productoData.vUUIDEmpresa = vUUIDEmpresa;
                }

                productoData.vLatLon = vLatLon.trim() || '';
                productoData.idCatCicloFacturacion = obtenerIdCicloFacturacion(renovacionSeleccionada.vTipoRenovacion);
                productoData.iDiasPeriodoPrueba = parseInt(iDiasPeriodoPrueba) || 0;

                if ((tipoLicenciaSeleccionado.idCatTipoLicencia === 2 ||
                    tipoLicenciaSeleccionado.idCatTipoLicencia === 4) &&
                    licenciaServidorVinculada) {
                    productoData.vUUIDLicenciaServidor = licenciaServidorVinculada.vUUIDLicencia;
                }
            }
        }

        // Configurar idLicencia según el tipo de licencia
        let idLicenciaParam = null;
        if (productoSeleccionado.vTipoProducto === 'Licencia' && tipoLicenciaSeleccionado) {
            if (tipoLicenciaSeleccionado.idCatTipoLicencia === 7) {
                // Para AkasiaPOS, usar la licencia seleccionada
                idLicenciaParam = licenciaAkasiaPOSSeleccionada.idLicencia;
            } else if (licenciaServidorVinculada) {
                // Para otros tipos con vinculación
                idLicenciaParam = licenciaServidorVinculada.idLicencia;
            }
        }

        // Generar nombre automático si está vacío: "Suscripcion {Periodicidad} {Moneda}"
        let nombreFinal = nombreSuscripcion.trim();
        if (!nombreFinal) {
            const tipoRenovacionCapitalizado = renovacionSeleccionada.vTipoRenovacion.charAt(0).toUpperCase() + 
                                               renovacionSeleccionada.vTipoRenovacion.slice(1).toLowerCase();
            const monedaFinal = monedaSeleccionada || (productoSeleccionado && productoSeleccionado.vMoneda) || 'MXN';
            nombreFinal = `Suscripcion ${tipoRenovacionCapitalizado} ${monedaFinal}`;
        }

        const body = {
            idCliente: idCliente,
            vNombreSuscripcion: nombreFinal,
            idLicencia: idLicenciaParam,
            vTipoRenovacion: renovacionSeleccionada.vTipoRenovacion,
            vMoneda: monedaSeleccionada, // Moneda seleccionada en el modal
            idCatFormaPago: 22, // Siempre automático (Stripe)
            productos: [productoData]
        };

        console.log('📤 Enviando datos de suscripción:', body);

        // Guardar payload para usar después en confirmación
        setPayloadSuscripcion(body);

        try {
            const response = await fetch('/api/Suscripciones/CrearNuevaSuscripcion', {
                method: 'POST',
                headers:
                    {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                const data = await response.json();
                
                // iResponseType === 3 significa que existe suscripción compatible
                if (data.iResponseType === 3) {
                    console.log('🔔 Suscripción existente encontrada:', data);
                    
                    // Parsear detalles de suscripción existente
                    const detalles = JSON.parse(data.detalles);
                    setInfoSuscripcionExistente(detalles.suscripcionExistente);
                    
                    // Mostrar modal de confirmación de pago
                    setShowModalConfirmacion(true);
                    setCargando(false);
                    return; // Esperar decisión del usuario
                }
                
                // iResponseType === 1 significa suscripción creada exitosamente
                if (data.iResponseType === 1) {

                    showSuccessToast(data.vMessage);
                    if (onSuscripcionCreada) onSuscripcionCreada();
                    handleClose();
                } else {

                    showErrorToast(data.vMessage || 'Error al crear la suscripción');
                }
            } else {

                showErrorToast(response.status + ' ' + response.statusText);
            }
        } catch (error) {

            showErrorToast('Error al crear la suscripción: ' + error.message);
        } finally {
            setCargando(false);
        }
    };

    /**
     * Handler para el modal de confirmación de pago
     * Ejecuta CrearNuevaSuscripcionConCobro con el parámetro cobrarAhora
     */
    const handleConfirmacionPago = async (cobrarAhora) => {
        setShowModalConfirmacion(false);
        setCargando(true);

        try {
            const payload = {
                suscripcionData: payloadSuscripcion,
                cobrarAhora: cobrarAhora
            };

            console.log('📤 Enviando con integración Stripe:', payload);

            const response = await fetch('/api/Suscripciones/CrearNuevaSuscripcionConCobro', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    showSuccessToast(data.vMessage);
                    if (onSuscripcionCreada) onSuscripcionCreada();
                    handleClose();
                } else {
                    showErrorToast(data.vMessage || 'Error al procesar la suscripción');
                }
            } else {
                showErrorToast(response.status + ' ' + response.statusText);
            }
        } catch (error) {
            showErrorToast('Error al procesar: ' + error.message);
        } finally {
            setCargando(false);
        }
    };

    return (
        <>
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static" centered>
            <Modal.Header>
                <Modal.Title>Nueva suscripción</Modal.Title>
                <FalconCloseButton className="btn btn-circle btn-sm transition-base p-0" onClick={handleClose} />
            </Modal.Header>

            <Modal.Body>
                <div className="d-flex" style={{ minHeight: '400px' }}>
                    {/* Panel izquierdo: Lista de productos */}
                    <div style={{ width: '45%', borderRight: '1px solid #eee' }} className="pe-3">
                        <h6 className="fw-bold">Productos</h6>
                        
                        {/* Buscador de productos */}
                        <div className="mb-2">
                            <Form.Control
                                type="text"
                                placeholder="Buscar por nombre o código..."
                                value={busquedaProducto}
                                onChange={(e) => setBusquedaProducto(e.target.value)}
                                size="sm"
                            />
                            {busquedaProducto && (
                                <div className="small text-muted mt-1">
                                    <FontAwesomeIcon icon="search" className="me-1" />
                                    {productosFiltrados.length} resultado{productosFiltrados.length !== 1 ? 's' : ''}
                                </div>
                            )}
                        </div>

                        <div className="mt-2" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {productos.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    <div className="mb-2" style={{ fontSize: '2rem', opacity: 0.3 }}>??</div>
                                    <div>No hay productos disponibles</div>
                                </div>
                            ) : productosFiltrados.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    <div className="mb-2" style={{ fontSize: '2rem', opacity: 0.3 }}>🔍</div>
                                    <div>No se encontraron productos</div>
                                    <div className="small">con "{busquedaProducto}"</div>
                                </div>
                            ) : (
                                productosFiltrados.map((p) => (
                                    <div key={p.idProducto}
                                        className={`p-2 rounded-2 mb-2 ${productoSeleccionado?.idProducto === p.idProducto ? 'bg-primary bg-opacity-10 border border-primary border-opacity-25' : 'border'}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => onSelectProducto(p)}
                                    >
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <div className="fw-600">{p.vNombreProducto}</div>
                                                <div className="small text-muted">{p.vCodigoBarras}</div>
                                                {p.vTipoProducto && (
                                                    <Badge bg={p.vTipoProducto === 'Licencia' ? 'info' : 'secondary'} className="mt-1">
                                                        {p.vTipoProducto}
                                                    </Badge>
                                                )}
                                            </div>
                                            <span className={`badge ${p.vMoneda === 'USD' ? 'bg-primary' : 'bg-success'}`}>
                                                {p.vMoneda}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    {/* Panel derecho: Detalles del producto seleccionado */}
                    <div className="flex-grow-1 ps-3">
                        {productoSeleccionado ? (
                            <div className="d-flex flex-column h-100">
                                <div className="mb-2">
                                    <h6 className="fw-bold mb-1">{productoSeleccionado.vNombreProducto}</h6>
                                    <div className="small text-muted">{productoSeleccionado.vCodigoBarras}</div>
                                </div>

                                {/* Nombre de la suscripción */}
                                <div className="mb-3">
                                    <Form.Group controlId="nombreSuscripcion">
                                        <Form.Label className="fw-bold">Nombre de la suscripción</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={nombreSuscripcion}
                                            onChange={(e) => setNombreSuscripcion(e.target.value)}
                                            placeholder="Ej. Suscripcion Mensual USD"
                                        />
                                        <Form.Text className="text-muted">
                                            <FontAwesomeIcon icon="info-circle" className="me-1" />
                                            {nombreSuscripcion ? (
                                                <>
                                                    Nombre predefinido: <strong>{nombreSuscripcion}</strong>.
                                                </>
                                            ) : (
                                                <>Opcional. Si se deja vacío, se generará automáticamente.</>
                                            )}
                                            <div className="small text-muted mt-1">
                                                Nota: Este es el nombre que verá el cliente al momento de pagar.
                                            </div>
                                        </Form.Text>
                                    </Form.Group>
                                </div>

                                {/* Selector de Moneda */}
                                <div className="mb-3">
                                    <Form.Group controlId="monedaSuscripcion">
                                        <Form.Label className="fw-bold">
                                            <FontAwesomeIcon icon="dollar-sign" className="me-2" />
                                            Moneda de la Suscripción *
                                        </Form.Label>
                                        <Form.Select
                                            value={monedaSeleccionada}
                                            onChange={(e) => setMonedaSeleccionada(e.target.value)}
                                        >
                                            <option value="MXN">MXN - Pesos Mexicanos</option>
                                            <option value="USD">USD - Dólares</option>
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            <FontAwesomeIcon icon="info-circle" className="me-1" />
                                            Selecciona la moneda en la que se cobrará esta suscripción
                                        </Form.Text>
                                    </Form.Group>
                                </div>

                                {/* 🔧 NUEVO: Si es producto tipo Licencia, mostrar campos adicionales */}
                                {productoSeleccionado.vTipoProducto === 'Licencia' && (
                                    <>
                                        {/* Selector de Tipo de Licencia */}
                                        <div className="mb-3">
                                            <Form.Group controlId="tipoLicencia">
                                                <Form.Label className="fw-bold">Tipo de Licencia *</Form.Label>
                                                <Form.Select
                                                    value={tipoLicenciaSeleccionado ? tipoLicenciaSeleccionado.idCatTipoLicencia : ''}
                                                    disabled={!!productoSeleccionado.idCatTipoLicencia}
                                                    onChange={(e) => {
                                                        const tipo = tiposLicencia.find(t => t.idCatTipoLicencia.toString() === e.target.value);
                                                        setTipoLicenciaSeleccionado(tipo || null);
                                                        setLicenciaServidorVinculada(null); // Limpiar vinculación al cambiar tipo
                                                        setLicenciaAkasiaPOSSeleccionada(null); // Limpiar licencia AkasiaPOS seleccionada
                                                    }}
                                                >
                                                    <option value="">Selecciona un tipo de licencia</option>
                                                    {tiposLicencia.map((tipo) => (
                                                        <option key={tipo.idCatTipoLicencia} value={tipo.idCatTipoLicencia}>
                                                            {tipo.vDescripcionTipo}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {productoSeleccionado.idCatTipoLicencia && (
                                                    <Form.Text className="text-muted">
                                                        <FontAwesomeIcon icon="lock" className="me-1" />
                                                        Tipo de licencia definido automáticamente por el producto
                                                    </Form.Text>
                                                )}
                                            </Form.Group>
                                        </div>

                                        {/* Si es tipo AkasiaPOS (7), mostrar selector de licencia existente */}
                                        {tipoLicenciaSeleccionado && tipoLicenciaSeleccionado.idCatTipoLicencia === 7 && (
                                            <div className="mb-3">
                                                <Form.Group controlId="licenciaAkasiaPOS">
                                                    <Form.Label className="fw-bold">
                                                        <FontAwesomeIcon icon="mobile-alt" className="me-2" />
                                                        Licencia AkasiaPOS *
                                                    </Form.Label>
                                                    {cargandoLicenciasAkasiaPOS && (
                                                        <div className="d-flex align-items-center">
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            <span className="text-muted">Cargando licencias AkasiaPOS...</span>
                                                        </div>
                                                    )}
                                                    {!cargandoLicenciasAkasiaPOS && licenciasAkasiaPOS.length === 0 && (
                                                        <Alert variant="warning" className="mb-2 py-2">
                                                            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                                                            No hay licencias AkasiaPOS disponibles. El cliente debe registrarse primero en AkasiaPOS.
                                                        </Alert>
                                                    )}
                                                    {!cargandoLicenciasAkasiaPOS && licenciasAkasiaPOS.length > 0 && (
                                                        <>
                                                            <Form.Select
                                                                value={licenciaAkasiaPOSSeleccionada ? licenciaAkasiaPOSSeleccionada.idLicencia : ''}
                                                                onChange={(e) => {
                                                                    const licencia = licenciasAkasiaPOS.find(lic => lic.idLicencia.toString() === e.target.value);
                                                                    setLicenciaAkasiaPOSSeleccionada(licencia || null);
                                                                }}
                                                            >
                                                                <option value="">Selecciona una licencia AkasiaPOS</option>
                                                                {licenciasAkasiaPOS.map((licencia) => (
                                                                    <option key={licencia.idLicencia} value={licencia.idLicencia}>
                                                                        {licencia.vNombreLicencia} (ID: {licencia.idLicencia})
                                                                        {licencia.bTieneSuscripcionActiva ? ' - ⚠️ Ya tiene suscripción' : ''}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                            {licenciaAkasiaPOSSeleccionada && (
                                                                <Alert variant={licenciaAkasiaPOSSeleccionada.bTieneSuscripcionActiva ? 'warning' : 'success'} className="mt-2 mb-0 py-2">
                                                                    {licenciaAkasiaPOSSeleccionada.bTieneSuscripcionActiva ? (
                                                                        <>
                                                                            <FontAwesomeIcon icon="exclamation-circle" className="me-2" />
                                                                            Esta licencia ya tiene suscripción activa: {licenciaAkasiaPOSSeleccionada.vNombreSuscripcionActiva}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FontAwesomeIcon icon="check-circle" className="me-2" />
                                                                            Licencia lista para suscripción
                                                                        </>
                                                                    )}
                                                                </Alert>
                                                            )}
                                                        </>
                                                    )}
                                                    <Form.Text className="text-muted">
                                                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                                                        La licencia debe haber sido creada desde el registro en AkasiaPOS
                                                    </Form.Text>
                                                </Form.Group>
                                            </div>
                                        )}

                                        {/* ?? Selector de Licencia Servidor (condicional) */}
                                        {debeVincularServidor() && (
                                            <div className="mb-3">
                                                <Form.Group controlId="licenciaServidor">
                                                    <Form.Label className="fw-bold">
                                                        Licencia Servidor {tipoLicenciaSeleccionado?.idCatTipoLicencia === 4 ? '*' : '(Opcional)'}
                                                    </Form.Label>
                                                    {cargandoLicenciasServidor ? (
                                                        <div className="d-flex align-items-center">
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            <span className="text-muted">Cargando licencias servidor...</span>
                                                        </div>
                                                    ) : licenciasServidor.length === 0 ? (
                                                        <Alert variant="warning" className="mb-2 py-2">
                                                            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                                                            No hay licencias tipo Servidor disponibles para este cliente
                                                        </Alert>
                                                    ) : (
                                                        <Form.Select
                                                            value={licenciaServidorVinculada ? licenciaServidorVinculada.idLicencia : ''}
                                                            onChange={(e) => {
                                                                const licencia = licenciasServidor.find(lic => lic.idLicencia.toString() === e.target.value);
                                                                setLicenciaServidorVinculada(licencia || null);
                                                            }}
                                                        >
                                                            <option value="">
                                                                {tipoLicenciaSeleccionado?.idCatTipoLicencia === 4
                                                                    ? 'Selecciona una licencia Servidor'
                                                                    : 'Sin vincular a Servidor'}
                                                            </option>
                                                            {licenciasServidor.map((licencia) => (
                                                                <option key={licencia.idLicencia} value={licencia.idLicencia}>
                                                                    ID: {licencia.idLicencia}
                                                                    {licencia.vUUIDEmpresa && ` - ${licencia.vUUIDEmpresa.substring(0, 8)}...`}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    )}
                                                    <Form.Text className="text-muted">
                                                        {tipoLicenciaSeleccionado?.idCatTipoLicencia === 4
                                                            ? 'Las licencias Caja deben vincularse a una licencia Servidor'
                                                            : 'Opcional: vincula esta licencia a un Servidor existente'}
                                                    </Form.Text>
                                                </Form.Group>
                                            </div>
                                        )}

                                        {/* UUIDs generados con botones para regenerar */}
                                        {vUUIDLicencia && vUUIDEmpresa && (
                                            <div className="mb-3">
                                                <div className="border rounded p-3 bg-light">
                                                    <div className="mb-2">
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <Form.Label className="mb-0 fw-bold small">UUID Licencia</Form.Label>
                                                            <Button
                                                                variant="falcon-primary"
                                                                size="sm"
                                                                onClick={regenerarUUIDLicencia}
                                                                className="py-0 px-2"
                                                            >
                                                                <FontAwesomeIcon icon="sync" className="me-1" />
                                                                Regenerar
                                                            </Button>
                                                        </div>
                                                        <Form.Control
                                                            type="text"
                                                            value={vUUIDLicencia}
                                                            readOnly
                                                            className="font-monospace small"
                                                        />
                                                    </div>

                                                    {/* Solo mostrar UUID Empresa si NO es tipo Nube */}
                                                    {tipoLicenciaSeleccionado?.idCatTipoLicencia !== 3 && (
                                                        <div>
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <Form.Label className="mb-0 fw-bold small">UUID Empresa</Form.Label>
                                                                <Button
                                                                    variant="falcon-primary"
                                                                    size="sm"
                                                                    onClick={regenerarUUIDEmpresa}
                                                                    className="py-0 px-2"
                                                                >
                                                                    <FontAwesomeIcon icon="sync" className="me-1" />
                                                                    Regenerar
                                                                </Button>
                                                            </div>
                                                            <Form.Control
                                                                type="text"
                                                                value={vUUIDEmpresa}
                                                                readOnly
                                                                className="font-monospace small"
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Mensaje informativo para tipo Nube */}
                                                    {tipoLicenciaSeleccionado?.idCatTipoLicencia === 3 && (
                                                        <Alert variant="info" className="py-2 px-3 mb-0 mt-2">
                                                            <FontAwesomeIcon icon="info-circle" className="me-2" />
                                                            <small>
                                                                {licenciaServidorVinculada
                                                                    ? 'El UUID Empresa se tomará de la licencia Servidor vinculada'
                                                                    : 'El UUID Empresa quedará vacío (sin vinculación a Servidor)'}
                                                            </small>
                                                        </Alert>
                                                    )}

                                                    <Form.Text className="text-muted d-block mt-2">
                                                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                                                        Los UUIDs se generan automáticamente. Puedes regenerarlos si es necesario.
                                                    </Form.Text>
                                                </div>
                                            </div>
                                        )}

                                        {/* Campo de Días de Periodo de Prueba - SOLO para Nube (tipo 3) */}
                                        {tipoLicenciaSeleccionado && tipoLicenciaSeleccionado.idCatTipoLicencia === 3 && (
                                            <div className="mb-3">
                                                <Form.Group controlId="iDiasPeriodoPrueba">
                                                    <Form.Label className="fw-bold">
                                                        Días de Periodo de Prueba *
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        max="365"
                                                        value={iDiasPeriodoPrueba}
                                                        onChange={(e) => setIDiasPeriodoPrueba(e.target.value)}
                                                        placeholder="Ej. 15"
                                                    />
                                                    <Form.Text className="text-muted">
                                                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                                                        Número de días que el cliente podrá usar la licencia sin costo (0 para sin periodo de prueba)
                                                    </Form.Text>
                                                </Form.Group>
                                            </div>
                                        )}

                                        {/* Campo de Latitud, Longitud (obligatorio SOLO para Servidor) */}
                                        {tipoLicenciaSeleccionado && tipoLicenciaSeleccionado.idCatTipoLicencia === 1 && (
                                            <div className="mb-3">
                                                <Form.Group controlId="vLatLon">
                                                    <Form.Label className="fw-bold">
                                                        Latitud, Longitud *
                                                        <a href="https://www.google.com.mx/maps" rel="noreferrer" target="_blank" className="ms-2 small">
                                                            <FontAwesomeIcon icon="map-marker-alt" className="me-1" />
                                                            Abrir Google Maps
                                                        </a>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={vLatLon}
                                                        onChange={(e) => setVLatLon(e.target.value)}
                                                        placeholder="Ej. 19.432608,-99.133209"
                                                        isInvalid={vLatLon.trim() !== '' && !/^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/.test(vLatLon.trim())}
                                                        autoComplete="off"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        Formato inválido. Use: latitud,longitud (Ej. 19.432608,-99.133209)
                                                    </Form.Control.Feedback>
                                                    <Form.Text className="text-muted">
                                                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                                                        Formato: latitud,longitud. Puedes obtenerlo desde Google Maps haciendo clic derecho en el mapa.
                                                    </Form.Text>
                                                </Form.Group>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Selector de Licencia Servidor para ModuloExtra (OPCIONAL) */}
                                {puedeVincularModuloExtra() && (
                                    <div className="mb-3">
                                        <Form.Group controlId="licenciaServidorModulo">
                                            <Form.Label className="fw-bold">
                                                Vincular a Licencia Servidor (Opcional)
                                            </Form.Label>
                                            {cargandoLicenciasServidor ? (
                                                <div className="d-flex align-items-center">
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    <span className="text-muted">Cargando licencias servidor...</span>
                                                </div>
                                            ) : licenciasServidor.length === 0 ? (
                                                <Alert variant="info" className="mb-2 py-2">
                                                    <FontAwesomeIcon icon="info-circle" className="me-2" />
                                                    No hay licencias tipo Servidor disponibles para este cliente
                                                </Alert>
                                            ) : (
                                                <Form.Select
                                                    value={licenciaServidorVinculada ? licenciaServidorVinculada.idLicencia : ''}
                                                    onChange={(e) => {
                                                        const licencia = licenciasServidor.find(lic => lic.idLicencia.toString() === e.target.value);
                                                        setLicenciaServidorVinculada(licencia || null);
                                                    }}
                                                >
                                                    <option value="">Sin vincular a Servidor</option>
                                                    {licenciasServidor.map((licencia) => (
                                                        <option key={licencia.idLicencia} value={licencia.idLicencia}>
                                                            ID: {licencia.idLicencia}
                                                            {licencia.vUUIDEmpresa && ` - ${licencia.vUUIDEmpresa.substring(0, 8)}...`}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            )}
                                            <Form.Text className="text-muted">
                                                Opcional: vincula este módulo a una licencia Servidor existente del cliente
                                            </Form.Text>
                                        </Form.Group>
                                    </div>
                                )}

                                {/* Tipos de renovación */}
                                <div className="flex-grow-1">
                                    <h6 className="fw-bold">Tipos de renovación *</h6>
                                    <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {renovaciones.length === 0 ? (
                                            <div className="text-muted small">Selecciona un producto para ver renovaciones disponibles</div>
                                        ) : (
                                            renovaciones.map(r => (
                                                <div key={r.idProductoRenovaciones}
                                                    className={`p-2 rounded-2 mb-2 d-flex justify-content-between align-items-center ${renovacionSeleccionada?.idProductoRenovaciones === r.idProductoRenovaciones ? 'bg-success bg-opacity-10 border border-success border-opacity-25' : 'border'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setRenovacionSeleccionada(r)}
                                                >
                                                    <div>
                                                        <div className="fw-600 text-capitalize">{r.vTipoRenovacion}</div>
                                                        <div className="small text-muted">
                                                            Precio: ${Number.parseFloat(r.dPrecioUnitario).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                        </div>
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="fw-bold text-success">
                                                            ${parseFloat(r.dTotal).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                        </div>
                                                        <div className="small text-muted">+ IVA</div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* Editor de Precio */}
                                {renovacionSeleccionada && (
                                    <div className="mt-3">
                                        <h6 className="fw-bold">Precio de la Suscripción</h6>
                                        <div className="row">
                                            <div className="col-md-12 mb-2">
                                                <Form.Group controlId="precioTotal">
                                                    <Form.Label className="fw-bold">
                                                        <FontAwesomeIcon icon="dollar-sign" className="me-2" />
                                                        Precio Total *
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={precioTotal}
                                                        onChange={(e) => recalcularPrecios(e.target.value)}
                                                        placeholder="0.00"
                                                    />
                                                    <Form.Text className="text-muted">
                                                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                                                        Edita el precio final. El precio sin IVA se calculará automáticamente
                                                    </Form.Text>
                                                </Form.Group>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <Form.Group>
                                                    <Form.Label className="small text-muted">Precio sin IVA</Form.Label>
                                                    <div className="p-2 bg-light rounded border">
                                                        ${precioUnitario.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                    </div>
                                                </Form.Group>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <Form.Group>
                                                    <Form.Label className="small text-muted">IVA (16%)</Form.Label>
                                                    <div className="p-2 bg-light rounded border">
                                                        ${precioIVA.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                    </div>
                                                </Form.Group>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Botón crear */}
                                <div className="text-center mt-3">
                                    <Button
                                        onClick={handleSeleccionar}
                                        variant='success'
                                        size="md"
                                        className="px-4"
                                        disabled={!validarFormulario() || cargando}
                                    >
                                        {cargando ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Creando...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon="check" className="me-2" />
                                                Crear Suscripción
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                                <div className="text-center text-muted">
                                    <div style={{ fontSize: '3rem', opacity: 0.3 }}>👈</div>
                                    <div>Selecciona un producto de la lista</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="pt-2 justify-content-between">
                <Button onClick={handleClose} variant='secondary' size="sm">
                    <FontAwesomeIcon icon="times" className="me-1" />
                    Cancelar
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Modal de confirmación de pago cuando existe suscripción compatible */}
        <ModalConfirmacionPagoSuscripcion
            show={showModalConfirmacion}
            onHide={() => setShowModalConfirmacion(false)}
            diferencia={infoSuscripcionExistente?.diferencia || 0}
            moneda={infoSuscripcionExistente?.moneda || 'MXN'}
            onConfirm={handleConfirmacionPago}
        />
        </>
    );
};

ModalNuevaSuscripcion.propTypes = {
    show: PropTypes.bool.isRequired,
    setShow: PropTypes.func.isRequired,
    idCliente: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSuscripcionCreada: PropTypes.func
};

export default ModalNuevaSuscripcion;
